// Shared with the startup publisher. Stored choices stay stable while the book's
// content language, rather than the interface locale, chooses the actual face.
export const readingProfiles = {
  latin: {name:'OpenDyslexic',family:'"Reading OpenDyslexic", "Reading Andika", "Reading Vazirmatn", system-ui, sans-serif',styles:['opendyslexic','andika'],letter:'normal',word:'normal'},
  arabic: {name:'Readex Pro',family:'"Reading Readex Pro", "Reading Vazirmatn", sans-serif',styles:['readex-pro','vazirmatn'],letter:'normal',word:'0.04em'},
  persian: {name:'Vazirmatn',family:'"Reading Vazirmatn", sans-serif',styles:['vazirmatn'],letter:'normal',word:'0.04em'},
  devanagari: {name:'Noto Sans Devanagari',family:'"Reading Noto Sans Devanagari", sans-serif',styles:['noto-sans-devanagari'],letter:'normal',word:'0.04em'},
  japanese: {name:'BIZ UDGothic',family:'"Reading BIZ UDGothic", "Hiragino Kaku Gothic ProN", Meiryo, sans-serif',styles:['biz-udgothic'],letter:'0.04em',word:'normal',lineBreak:'strict'},
  korean: {name:'Pretendard',family:'"Reading Pretendard", "Apple SD Gothic Neo", "Malgun Gothic", sans-serif',styles:['pretendard'],letter:'0.02em',word:'0.06em',wordBreak:'keep-all'},
  simplified: {name:'Noto Sans SC',family:'"Reading Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif',styles:['noto-sans-sc'],letter:'0.06em',word:'normal',lineBreak:'strict'},
  traditional: {name:'Noto Sans TC',family:'"Reading Noto Sans TC", "PingFang TC", "Microsoft JhengHei", sans-serif',styles:['noto-sans-tc'],letter:'0.06em',word:'normal',lineBreak:'strict'},
  hongkong: {name:'Noto Sans HK',family:'"Reading Noto Sans HK", "PingFang HK", "Microsoft JhengHei", sans-serif',styles:['noto-sans-hk'],letter:'0.06em',word:'normal',lineBreak:'strict'},
  thai: {name:"Noto Sans Thai",family:'"Reading Noto Sans Thai", Tahoma, sans-serif',styles:["noto-sans-thai"],letter:"normal",word:"normal"},
  system: {name:'System sans serif',family:'system-ui, sans-serif',styles:[],letter:'normal',word:'normal'}
};

export function dyslexiaProfile(language='en') {
  let locale;
  try {locale=new Intl.Locale(language);} catch {return readingProfiles.system;}
  const script=locale.script||locale.maximize().script,base=locale.language;
  if(script==='Thai')return readingProfiles.thai;
  if(script==='Jpan')return readingProfiles.japanese;
  if(script==='Kore'||script==='Hang')return readingProfiles.korean;
  if(script==='Hans')return readingProfiles.simplified;
  if(script==='Hant')return ['HK','MO'].includes(locale.region)?readingProfiles.hongkong:readingProfiles.traditional;
  if(script==='Arab')return base==='ar'?readingProfiles.arabic:readingProfiles.persian;
  if(script==='Deva')return readingProfiles.devanagari;
  if(script==='Cyrl'||script==='Latn')return readingProfiles.latin;
  return readingProfiles.system;
}

export const readingStylesheets = profile => profile.styles.map(id=>`fonts/reading/${id}/index.css`);

function textFace(name,id,fallback,wrapping={}) {
  return {name,family:`"Reading ${name}", ${fallback}`,styles:[id],letter:'normal',word:'normal',...wrapping};
}

const serifFaces={
  japanese:textFace('Noto Serif JP','noto-serif-jp','"Hiragino Mincho ProN", "Yu Mincho", serif',{lineBreak:'strict'}),
  korean:textFace('Noto Serif KR','noto-serif-kr','"AppleMyungjo", Batang, serif',{wordBreak:'keep-all'}),
  simplified:textFace('Noto Serif SC','noto-serif-sc','"Songti SC", SimSun, serif',{lineBreak:'strict'}),
  traditional:textFace('Noto Serif TC','noto-serif-tc','"Songti TC", "PMingLiU", serif',{lineBreak:'strict'}),
  hongkong:textFace('Noto Serif HK','noto-serif-hk','"Songti TC", "MingLiU_HKSCS", serif',{lineBreak:'strict'}),
  arabic:textFace('Noto Naskh Arabic','noto-naskh-arabic','"Traditional Arabic", serif'),
  devanagari:textFace('Noto Serif Devanagari','noto-serif-devanagari','serif')
};
serifFaces.persian=serifFaces.arabic;
const sansFaces={
  japanese:textFace('Noto Sans JP','noto-sans-jp','"Hiragino Kaku Gothic ProN", Meiryo, sans-serif',{lineBreak:'strict'}),
  korean:textFace('Noto Sans KR','noto-sans-kr','"Apple SD Gothic Neo", "Malgun Gothic", sans-serif',{wordBreak:'keep-all'}),
  simplified:textFace('Noto Sans SC','noto-sans-sc','"PingFang SC", "Microsoft YaHei", sans-serif',{lineBreak:'strict'}),
  traditional:textFace('Noto Sans TC','noto-sans-tc','"PingFang TC", "Microsoft JhengHei", sans-serif',{lineBreak:'strict'}),
  hongkong:textFace('Noto Sans HK','noto-sans-hk','"PingFang HK", "Microsoft JhengHei", sans-serif',{lineBreak:'strict'}),
  arabic:textFace('Noto Sans Arabic','noto-sans-arabic','sans-serif'),
  persian:textFace('Vazirmatn','vazirmatn','sans-serif'),
  devanagari:textFace('Noto Sans Devanagari','noto-sans-devanagari','sans-serif')
};
const latinSerif={name:'Literata',family:'"Literata Variable", Literata, Georgia, serif',styles:[],letter:'normal',word:'normal'};
const latinSans={name:'Inter',family:'Inter, system-ui, sans-serif',styles:[],letter:'normal',word:'normal'};

export function readingTypography(family,language='en') {
  const script=dyslexiaProfile(language);
  if(script===readingProfiles.thai)return script;
  if(family==='dyslexic'||family==='readex')return script;
  const key=Object.keys(readingProfiles).find(key=>readingProfiles[key]===script);
  if(family==='literata')return serifFaces[key]||latinSerif;
  if(family==='inter')return sansFaces[key]||latinSans;
  return {...readingProfiles.system,lineBreak:script.lineBreak,wordBreak:script.wordBreak};
}
